import React, { useEffect, useState } from 'react';
import api from '../../../utils/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { fmtMoney } from './kpiUtils';
import { DollarSign, Loader2, TrendingUp, AlertTriangle } from 'lucide-react';

const ESTADO_META = {
  abonada: { dot: 'bg-emerald-500', label: 'Abonada', text: 'text-emerald-600' },
  mora: { dot: 'bg-rose-500', label: 'En mora', text: 'text-rose-600' },
  pendiente: { dot: 'bg-slate-400', label: 'Pendiente', text: 'text-slate-500' },
};

const BreakdownCard = ({ label, value, sub, testId }) => (
  <div className="bg-white/70 border border-emerald-200 rounded-lg p-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-medium">{label}</p>
    <p className="text-base font-bold text-slate-800 font-mono mt-0.5 tabular-nums">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const MoraCard = ({ label, value, sub, testId }) => (
  <div className="bg-white/70 border border-rose-200 rounded-lg p-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-rose-700/70 font-medium">{label}</p>
    <p className="text-base font-bold text-slate-800 font-mono mt-0.5 tabular-nums">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const PrestamoGananciaModal = ({ prestamoId, open, onClose }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !prestamoId) return;
    let cancel = false;
    setLoading(true);
    setD(null);
    api.get(`/kpis/prestamos/detalle/${prestamoId}`)
      .then((r) => { if (!cancel) setD(r.data); })
      .catch((e) => { if (!cancel) { toast.error(e.response?.data?.detail || 'No se pudo cargar el detalle'); onClose?.(); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, prestamoId, onClose]);

  const pct = d ? Math.max(0, Math.min(100, d.pct_realizado)) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="prestamo-ganancia-modal">
        <DialogHeader>
          <DialogTitle>Detalle del préstamo {d?.prestamo_id ? `#${d.prestamo_id}` : ''}</DialogTitle>
          <DialogDescription>Ingresos realizados, mora consolidada y detalle de cuotas del préstamo.</DialogDescription>
        </DialogHeader>

        {loading || !d ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Cargando…</div>
        ) : (
          <div className="space-y-5 min-w-0 w-full">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="font-semibold text-slate-800">{d.nombre}</span>
              <span className="font-mono text-xs text-slate-500">{d.cuit}</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-500">{d.frecuencia}</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-500">{d.cantidad_cuotas} cuotas</span>
              {d.plazo && <><span className="text-slate-400">·</span><span className="text-slate-500">{d.plazo}</span></>}
              {d.estado && <Badge variant="secondary" className="ml-1">{d.estado}</Badge>}
            </div>

            {/* Card verde de ingresos */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 p-5" data-testid="prestamo-ganancia-card">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2.5 rounded-full bg-emerald-600 shrink-0"><DollarSign className="text-white" size={20} /></div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-emerald-900">Ingresos del préstamo</h3>
                    <p className="text-xs text-emerald-700/70 mt-0.5">Ganancia por cuota cerrada (total − capital) + punitorios cobrados</p>
                  </div>
                </div>
                <div className="text-left sm:text-right shrink-0">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-medium">Ganancia realizada</p>
                  <p className="text-xl sm:text-2xl font-bold font-mono text-emerald-700 tabular-nums" data-testid="prestamo-ganancia-realizada">{fmtMoney(d.ingreso_realizado)}</p>
                </div>
              </div>

              <div className="mt-4">
                <div className="h-2 w-full rounded-full bg-emerald-200 overflow-hidden">
                  <div className="h-full bg-emerald-600 transition-all duration-700" style={{ width: `${pct}%` }} data-testid="prestamo-ganancia-barra" />
                </div>
                <p className="text-xs text-emerald-800 mt-1.5">
                  <span className="font-semibold">{d.pct_realizado}%</span> de la ganancia esperada ({fmtMoney(d.ganancia_esperada)})
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <BreakdownCard label="Capital prestado" value={fmtMoney(d.monto_solicitado)} sub={`÷ ${d.cantidad_cuotas} cuotas`} testId="bd-capital" />
                <BreakdownCard label="Capital por cuota" value={fmtMoney(d.capital_por_cuota)} sub="valor cuota pura" testId="bd-capital-cuota" />
                <BreakdownCard label="Ganancia · cuotas" value={fmtMoney(d.ganancia_cuotas)} sub={`${d.cuotas_pagadas}/${d.cuotas_normales} cuotas pagas`} testId="bd-ganancia-cuotas" />
                <BreakdownCard label="Ganancia · punitorios" value={fmtMoney(d.ganancia_punitorios)} sub={d.n_punitorios ? `${d.n_punitorios} fila(s)` : 'sin punitorios'} testId="bd-ganancia-punitorios" />
              </div>
            </div>

            {/* Panel de MORA consolidada */}
            {d.en_mora && (
              <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-rose-100/40 border border-rose-200 p-5" data-testid="prestamo-mora-panel">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="p-2.5 rounded-full bg-rose-600 shrink-0"><AlertTriangle className="text-white" size={20} /></div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-rose-900">Mora consolidada</h3>
                      <p className="text-xs text-rose-700/70 mt-0.5">Deuda vencida (capital + punitorios). En mora desde {d.fecha_mora || '—'}</p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right shrink-0">
                    <p className="text-[11px] uppercase tracking-wide text-rose-700/70 font-medium">Total en mora</p>
                    <p className="text-xl sm:text-2xl font-bold font-mono text-rose-700 tabular-nums" data-testid="prestamo-mora-total">{fmtMoney(d.total_mora)}</p>
                    <p className="text-xs text-rose-800 mt-0.5">{d.dias_mora} días en mora</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <MoraCard label="Capital en mora" value={fmtMoney(d.capital_mora)} sub={`${d.cuotas_vencidas} cuota(s) vencida(s)`} testId="mora-capital" />
                  <MoraCard label="Punitorios (saldo)" value={fmtMoney(d.punitorios_saldo)} sub={d.n_punitorios ? `${d.n_punitorios} fila(s)` : 'sin punitorios'} testId="mora-punitorios" />
                  <MoraCard label="Días en mora" value={`${d.dias_mora}`} sub={`desde ${d.fecha_mora || '—'}`} testId="mora-dias" />
                  <MoraCard label="Recupero esperado" value={fmtMoney(d.recupero_esperado)} sub={`${d.cuotas_restantes} cuota(s) por cobrar`} testId="mora-recupero" />
                </div>
              </div>
            )}

            {/* Tabla de cuotas */}
            <div className="rounded-lg border border-slate-200 overflow-hidden min-w-0">
              <div className="overflow-x-auto min-w-0">
                <table className="w-full text-sm" data-testid="prestamo-cuotas-tabla">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500">
                      <th className="text-left font-medium px-3 py-2">Vencimiento</th>
                      <th className="text-left font-medium px-3 py-2">Cuota</th>
                      <th className="text-right font-medium px-3 py-2">Total</th>
                      <th className="text-right font-medium px-3 py-2">Saldo</th>
                      <th className="text-right font-medium px-3 py-2">Ganancia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.cuotas.map((c, i) => {
                      const em = ESTADO_META[c.estado] || ESTADO_META.pendiente;
                      const pagosTxt = (c.pagos || []).map((p) => `${p.fecha}: ${fmtMoney(p.monto)}`).join(' · ');
                      return (
                        <tr key={i} className={`border-t border-slate-100 ${c.es_punitorio ? 'bg-indigo-50/50' : ''}`} data-testid={`cuota-row-${i}`}>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${em.dot}`} title={em.label} />
                              <span className="text-slate-600">{c.fecha_vto || '—'}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-slate-700">{c.es_punitorio ? 'Punitorios' : c.nro}</span>
                            {c.es_punitorio && <Badge className="ml-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px] px-1.5 py-0">·PUN</Badge>}
                            {pagosTxt && <p className="text-[11px] text-slate-400 mt-0.5">Pagos: {pagosTxt}</p>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmtMoney(c.total)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${c.saldo <= 0.005 ? 'text-emerald-600 font-medium' : em.text}`}>{fmtMoney(c.saldo)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {c.ganancia > 0
                              ? <span className="text-emerald-700 font-semibold">{fmtMoney(c.ganancia)}</span>
                              : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-emerald-200 bg-emerald-50/40 font-semibold">
                      <td className="px-3 py-2 text-emerald-800" colSpan={4}><span className="inline-flex items-center gap-1.5"><TrendingUp size={14} /> Ingreso realizado total</span></td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700" data-testid="prestamo-cuotas-total">{fmtMoney(d.ingreso_realizado)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrestamoGananciaModal;
